/* import __COLOCATED_TEMPLATE__ from './company-payments.hbs'; */
import { sort, readOnly } from '@ember/object/computed';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import Component from '@ember/component';
import { showModal } from "sh-www/utils/modal-controls";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: 'section',
  classNames: ['SH-c-company-payments'],
  classNameBindings: ['state.isAddingNewCard:SH-c-company-payments--loading'],

  /**
   * Register Services
   */
  errorTracker: service('error-tracker'),
  state: service('states/routes/settings/company/payments'),
  stateManager: service('state-managers/routes/settings/company/payments'),
  companyCommandService: service('commands/company-commands'),
  stripe: service(),
  session: service(),

  /**
   * @type {Boolean}    True if we are able to delete cards.
   */
  _canDeleteCard: computed('_cards.length', function() {
    return this.get('_cards.length') >= 2;
  }),

  /**
   * @type {Boolean}    True if we are able to make ANY of the cards a default card.
   */
  _canMakeCardDefault: computed('_cards.length', function() {
    return this.get('_cards.length') >= 2;
  }),

  /**
   * This will observe the properties on the array of cards and update accordingly.
   *
   * NOTE - Should probably use _cards for any operations as this is just to keep our array up to date.
   *
   * @type {Ember.MutableArray<Card>}
   */
  _cardsObserving: computed(
  'state.updatingCardId',
  'state.company.cards.[]',
  'state.company.cards.@each.isDefault',
  function() {
    return this.get('state.company.cards');
  }),

  /**
   * The array of cards for this company sorted by isDefault or not.
   *
   * @type {Ember.MutableArray<Card>}
   */
  _cards: sort(
  '_cardsObserving',
  function(cardA, cardB) {
    if (cardA.get('isDefault') === cardB.get('isDefault')) {
      return 0;
    }

    return cardA.get('isDefault') && !cardB.get('isDefault') ? -1 : 1;
  }),

  /**
   * @type {String}     The stripe token for the card entered by the user.
   */
  _stripeTokenValue: readOnly('state.stripeTokenForNewCard'),

  initializeData: task(function *() {
    const companyId = this.company.id;
    const company = this.stateManager.manageCompany(companyId);
    const stripe = this.stripe.load();
    try {
      yield Promise.all([stripe, company]);
    } catch(error) {
      handleErrorResponseWithModal(
        this, 
        error, 
        "There was a problem loading company billing. Please try again or contact support if the problem continues.",
        `initializeData -- error loading billing for company id: ${companyId}`,
        "Error loading the page"
      );
      throw error;
    }
  }).drop(),

  handleAddNewCard: task(function *(cardElement) {
    const { currentUser } = this.session;
    const company = this.state.company;

    try {
      const token = yield this.stripe.createToken(cardElement);
      const card = yield this.companyCommandService.addNewCardForCompany(company, token.id);
      cardElement.clear();
      yield this.companyCommandService.makeCardDefault(card);
      yield this.session.loadCurrentUser();
    } catch (e) {
      this.errorTracker.error(e, this, `error adding credit card to company [${company.id}] with user [${currentUser.id}]`);
      throw e;
    }
  }).enqueue(),

  actions: {
    /**
     * When user clicks on the 'delete card' for a specific card.
     *
     * @param {Card} card
     */
    onDeleteClicked: function(card) {
      if (this.get('state.updatingCardId') || this.get('state.isAddingNewCard')) {
        return;
      }

      let modalModel = {
        isShowCancelButton: true,
        titleText: `Delete Card`,
        bodyText: 'Are you sure you want to delete this card?',
        confirmAction: () => {
          this.get('stateManager').deleteCard(card);
        }
      };

      showModal(this, 'confirmation', modalModel);
    },

    /**
     * User clicks to change a card to the default.
     *
     * @param {Card} card
     */
    onMakeCardDefault: function(card) {
      if (this.get('state.updatingCardId') || this.get('state.isAddingNewCard')) {
        return;
      }

      this.get('stateManager').makeCardDefault(card);
    },
  }
});
